import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import type { GallerySettingsFormGroup } from '@web/widgets/product-card/type';
import { AspectRatio, type GallerySettingsFormType } from '@web/widgets/product-card/type';
import { ThemeStateService } from '@sites/site-theme/src/state';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import { CSS_RESTRICTIONS } from '@common/constants';
import { Attachment, CardKindViewType } from '@web/widgets/product-card/type/product-card-widget.type';
import { TranslateModule } from '@ngx-translate/core';
import { SpSwitchComponent } from '@ui/sp-switch';
import { SpInputNumComponent } from '@components/sp-form-controls/sp-input-num/sp-input-num.component';
import { SpColorPickerModule } from '@components/sp-color-picker/src/sp-color-picker.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'web-gallery-settings',
    templateUrl: './gallery-settings.component.html',
    styleUrls: ['./gallery-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        SpColorPickerModule,
        SpInputNumComponent,
        SpSwitchComponent,
        TranslateModule,
    ],
})
export class GallerySettingsComponent {
    private readonly themeState = inject(ThemeStateService);

    @Input() gallerySettingsFormGroup: GallerySettingsFormGroup;
    @Input() widgetId;
    @Input() cardKindViewType: CardKindViewType;
    @Input() isPreviewSettings: boolean = false;

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public attachmentOptions = Object.values(Attachment);
    public aspectRatioOptions = Object.values(AspectRatio);

    public cardKindViewTypeOptions = CardKindViewType;

    public setThemeBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.gallerySettingsFormGroup.markAsTouched({ onlySelf: true });

        this.backgroundColor.patchValue(presetName ?? value);
    }

    public setThemeButtonColor({ value, presetName }: ColorPickerEventPayload): void {
        this.gallerySettingsFormGroup.markAsTouched({ onlySelf: true });

        this.buttonColor.patchValue(presetName ?? value);
    }

    public setThemeButtonBackground({ value, presetName }: ColorPickerEventPayload): void {
        this.gallerySettingsFormGroup.markAsTouched({ onlySelf: true });

        this.buttonBackground.patchValue(presetName ?? value);
    }

    public setThemePaginationColor({ value, presetName }: ColorPickerEventPayload): void {
        this.gallerySettingsFormGroup.markAsTouched({ onlySelf: true });

        this.paginationColor.patchValue(presetName ?? value);
    }

    public setMargin(value: number): void {
        this.gallerySettingsFormGroup.markAsTouched({ onlySelf: true });

        this.margin.patchValue(value);
    }

    public setBorderRadius(value: number): void {
        this.gallerySettingsFormGroup.markAsTouched({ onlySelf: true });

        this.borderRadius.patchValue(value);
    }

    public setRatio(event) {
        const swiperWrapper = document.querySelector(`[id='${this.widgetId}'] .swiper-wrapper`) as HTMLDivElement;

        if (swiperWrapper) {
            if (event.target.value !== AspectRatio.default) {
                swiperWrapper.style.removeProperty('height');
            } else {
                setTimeout(() => {
                    swiperWrapper.style.height = 'auto';
                }, 100);
            }
        }
    }

    public get aspectRatio(): GallerySettingsFormType['aspectRatio'] {
        return this.gallerySettingsFormGroup.get('aspectRatio') as GallerySettingsFormType['aspectRatio'];
    }

    public get attachment(): GallerySettingsFormType['attachment'] {
        return this.gallerySettingsFormGroup.get('attachment') as GallerySettingsFormType['attachment'];
    }

    public get backgroundColor(): GallerySettingsFormType['backgroundColor'] {
        return this.gallerySettingsFormGroup.get('backgroundColor') as GallerySettingsFormType['backgroundColor'];
    }

    public get borderRadius(): GallerySettingsFormType['borderRadius'] {
        return this.gallerySettingsFormGroup.get('borderRadius') as GallerySettingsFormType['borderRadius'];
    }

    public get margin(): GallerySettingsFormType['margin'] {
        return this.gallerySettingsFormGroup.get('margin') as GallerySettingsFormType['margin'];
    }

    public get buttonColor(): GallerySettingsFormType['buttonColor'] {
        return this.gallerySettingsFormGroup.get('buttonColor') as GallerySettingsFormType['buttonColor'];
    }

    public get buttonBackground(): GallerySettingsFormType['buttonBackground'] {
        return this.gallerySettingsFormGroup.get('buttonBackground') as GallerySettingsFormType['buttonBackground'];
    }

    public get paginationColor(): GallerySettingsFormType['paginationColor'] {
        return this.gallerySettingsFormGroup.get('paginationColor') as GallerySettingsFormType['paginationColor'];
    }

    public get changeSlide(): GallerySettingsFormType['changeSlide'] {
        return this.gallerySettingsFormGroup.get('changeSlide') as GallerySettingsFormType['changeSlide'];
    }

    public get fullByClick(): GallerySettingsFormType['fullByClick'] {
        return this.gallerySettingsFormGroup.get('fullByClick') as GallerySettingsFormType['fullByClick'];
    }

    public get BORDER_RADIUS_MIN(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    }

    public get BORDER_RADIUS_MAX(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    }
}
